import { PersonClient } from '../../clients/stratus/tntCommonResourceRegistry';
import AccountsClient from '../../clients/stratus/accountmgtsvc/AccountsClient';
import { ISessionService } from '../session';
import IUserOnboardingService from './IUserOnboardingService';
import { DEFAULT_INITIAL_CURRENT_PAGE } from '../../clients/stratus/tntCommonResourceRegistry/constants';
import bindAllMethods from '../../utils/bindAllMethods';
import { UserOnboardingJoinInvitedOrganizationParams } from './types';
import { SetServiceDependencies } from '../../infra/startup/types';
import BaseUserOnboardingService from './BaseUserOnboardingService';
import { ILocalizationService } from '../localizationService';
import { IUserService } from '../userService';

export type StratusUserOnboardingServiceParams = {
  sessionService: ISessionService;
  stratusAccountMgtSvcClient: AccountsClient;
  stratusTntResourceRegistryPersonClient: PersonClient;
};

export default class StratusUserOnboardingService
  extends BaseUserOnboardingService
  implements IUserOnboardingService
{
  private _DEFAULT_ONBOARDING_ORG_TYPE = 'Personal';

  private _sessionService: ISessionService;
  private _localizationService: ILocalizationService;
  private _userService: IUserService;
  private _stratusAccountMgtSvcClient: AccountsClient;
  private _stratusTntResourceRegistryPersonClient: PersonClient;

  public setDependencies({
    services,
    clients,
    repositories
  }: SetServiceDependencies): void {
    super.setDependencies({ services, clients, repositories });

    const { sessionService, localizationService, userService } = services;

    const { stratus } = clients;
    this._sessionService = sessionService;
    this._localizationService = localizationService;
    this._userService = userService;
    this._stratusAccountMgtSvcClient = stratus.accountsMgtSvcClient;
    this._stratusTntResourceRegistryPersonClient = stratus.personClient;
    bindAllMethods(this);
  }

  public async shouldRenderOnboardingScreen(): Promise<boolean> {
    return true;
  }

  public async isUserOnboarded(): Promise<boolean> {
    this.throwIfInFallbackState('check onboarding status');

    const personDomains =
      await this._stratusTntResourceRegistryPersonClient.getPersonDomain({
        page: DEFAULT_INITIAL_CURRENT_PAGE
      });

    return !!(personDomains?.content?.length > 0);
  }

  public async onboardUser(): Promise<void> {
    this.throwIfInFallbackState('onboard user');

    const idToken = this._sessionService.getIdToken();
    const language = this._localizationService.language.toLowerCase();
    const residenceCountry =
      (await this._userService.getResidenceCountry()) || 'US';

    await this._stratusAccountMgtSvcClient.createAccount({
      accountId: null,
      accountName: null,
      language,
      countrySet: [residenceCountry],
      idToken,
      type: this._DEFAULT_ONBOARDING_ORG_TYPE
    });
  }

  public async joinInvitedOrganization(
    options?: UserOnboardingJoinInvitedOrganizationParams
  ): Promise<void> {
    this.throwIfInFallbackState('join organization');

    const idToken = this._sessionService.getIdToken();
    const language = this._localizationService.language.toLowerCase();
    const residenceCountry =
      (await this._userService.getResidenceCountry()) || 'US';

    await this._stratusAccountMgtSvcClient.createAccount({
      language,
      countrySet: [residenceCountry],
      idToken,
      accountId: options?.tenantId
    });
  }
}
